import type WaDataGrid from "@web.awesome.me/webawesome-pro/dist/components/data-grid/data-grid.js";
import type WaDialog from "@web.awesome.me/webawesome-pro/dist/components/dialog/dialog.js";
import type WaPage from "@web.awesome.me/webawesome-pro/dist/components/page/page.js";
import htmx from "htmx.org";
import { html } from "lit";
import { checkNotNil } from "../../check-not-nil.js";
import { JsonBootstrapper } from "../../handlers/json-bootstrapper.js";
import { stateProperty } from "../../property.js";
import type { ParserSchema, ParserType } from "../../schemas/parser.js";
import { Tags as Model, Tag } from "../../schemas/tag.js";
import { gridSearch } from "../../search.js";
import { WmElement } from "../element.js";

type Tags = ParserType<typeof Tags>;
const Tags = stateProperty(Model.schema, { defaultValue: [] });

export class WmTagsGrid extends WmElement {
  static readonly TAG_NAME = "wm-tags-grid";

  static properties = {
    tags: Tags,
    isMobile: { attribute: false },
  };

  declare tags: Tags;
  declare isMobile: boolean;
  #pageObserver?: MutationObserver;
  #jsonBootstrapper: JsonBootstrapper<ParserSchema<typeof Tags>>;

  constructor() {
    super();
    this.#jsonBootstrapper = new JsonBootstrapper({
      schema: Tags.schema,
      callback: (v) => (this.tags = v),
      logger: this.logger,
    });
  }

  connectedCallback() {
    super.connectedCallback();
    this.#initializePageView();
  }

  disconnectedCallback() {
    this.#pageObserver?.disconnect();
    this.#pageObserver = undefined;
    super.disconnectedCallback();
  }

  protected render() {
    return html`
      <slot @slotchange=${(e: Event) => this.#jsonBootstrapper.slotChanged(e)}></slot>
      ${this.#renderGrid()}`;
  }

  #renderGrid() {
    return html`
      <wa-dialog label="Are you sure?">
      	This action is irreversible.
      	<div slot="footer" class="wa-cluster">
      		<wa-button variant="brand" data-dialog="close">Cancel</wa-button>
      		<wa-button id="deletion-confirmation" variant="danger" @click=${this.#confirmDeletion}>Confirm</wa-button>
      	</div>
      </wa-dialog>
      <wa-data-grid
        label="Tags"
        paginate
        page-size="10"
        resizable
        row-key="id"
        size="s"
        striped
        with-search
      >
        <span slot="empty">No tags created yet.</span>
      </wa-data-grid>`;
  }

  protected updated(): void {
    this.#handleUpdated();
  }

  async #handleUpdated() {
    const dataGrid = this.#queryGrid();
    await dataGrid.updateComplete;
    dataGrid.searchFn = gridSearch;
    dataGrid.columns = [
      {
        field: "name",
        label: "Name",
        formatter: (v, row) =>
          html`<a href="/app/tags/${row.id}" @click=${this.#nameClickHandler(row.id as string)}>${v}</a>`,
      },
      {
        label: "Preview",
        formatter: (_, row) => Tag.format(row as Tag, { size: "s" }),
      },
      {
        id: "actions",
        label: "Actions",
        hidden: this.isMobile,
        formatter: (_, row) => html`
          <wm-delete-button size="xs" as-icon @click=${() => this.#promptDeletionConfirmation(row.id as string)}></wm-delete-button>
        `,
      },
    ];
    dataGrid.rowDetail = (row) => {
      const tag = row as Tag;
      return html`
        <div class="wa-grid" style="--min-column-size: 14ch; gap: var(--wa-space-l);">
          <div>
            <small style="color: var(--wa-color-text-quiet);">Name</small><br />
            <strong>${tag.name}</strong>
          </div>
          <div>
            <small style="color: var(--wa-color-text-quiet);">Preview</small><br />
            ${Tag.format(tag, { size: "xs" })}
          </div>
          <div>
            <small style="color: var(--wa-color-text-quiet);">Actions</small><br />
            <div class="wa-cluster">
              <wa-button size="xs" variant="success" @click=${() => htmx.ajax("GET", `/app/tags/${tag.id}`, { target: "body", push: "true" })}>Edit</wa-button>
              <wm-delete-button size="xs" @click=${() => this.#promptDeletionConfirmation(row.id as string)}></wm-delete-button>
            </div>
          </div>
        </div>
     `;
    };
    dataGrid.data = this.tags;
  }

  #confirmDeletion() {
    const dialog = this.#queryDialog();
    const tagId = dialog.dataset.tagId;
    htmx.ajax("DELETE", `/app/tags/${tagId}`);
  }

  #nameClickHandler(id: string) {
    return (e: PointerEvent) => {
      e.stopPropagation();
      e.preventDefault();
      htmx.ajax("GET", `/app/tags/${id}`, {
        target: "body",
        push: "true",
      });
    };
  }

  #promptDeletionConfirmation(tagId: string) {
    const dialog = this.#queryDialog();
    dialog.dataset.tagId = tagId;
    dialog.open = true;
  }

  #initializePageView() {
    // At this time, we actually consider it an error if no wraping wa-page
    // can be found.
    const page = this.#queryPage();
    this.isMobile = page.view === "mobile";
    this.#pageObserver = new MutationObserver(() => {
      this.isMobile = page.view === "mobile";
    });
    this.#pageObserver.observe(page, {
      attributes: true,
      attributeFilter: ["view"],
    });
  }

  #queryDialog(): WaDialog {
    return this.queryNotNil("wa-dialog");
  }

  #queryGrid(): WaDataGrid {
    return this.queryNotNil("wa-data-grid");
  }

  #queryPage(): WaPage {
    return checkNotNil(document.querySelector("wa-page"));
  }
}

customElements.define(WmTagsGrid.TAG_NAME, WmTagsGrid);

declare global {
  interface HTMLElementTagNameMap {
    [WmTagsGrid.TAG_NAME]: WmTagsGrid;
  }
}
